import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { ArrowLeft, Plus, Check } from 'lucide-react';
import Auth from './Auth';
import { useI18n } from '../i18n';

export default function CreateProjectFlow({ onBack, onSuccess }) {
  const { t } = useI18n();
  const [step, setStep] = useState('auth');
  const [session, setSession] = useState(null);
  const [nomeProjeto, setNomeProjeto] = useState('');
  const [finalidade, setFinalidade] = useState('');
  const [finalidadeOutro, setFinalidadeOutro] = useState('');
  const [temFilhos, setTemFilhos] = useState('');
  const [temAnimais, setTemAnimais] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setSession(session);
        setStep('projeto');
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setSession(session);
        setStep('projeto');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const criarProjeto = async () => {
    if (!nomeProjeto.trim()) {
      setMessage(t('createProject.errors.nameRequired'));
      return;
    }

    if (!finalidade) {
      setMessage(t('createProject.errors.purposeRequired'));
      return;
    }

    if (finalidade === 'outro' && !finalidadeOutro.trim()) {
      setMessage(t('createProject.errors.purposeOtherRequired'));
      return;
    }

    if (!temFilhos) {
      setMessage(t('createProject.errors.kidsRequired'));
      return;
    }

    if (!temAnimais) {
      setMessage(t('createProject.errors.petsRequired'));
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const projetoData = {
        nome: nomeProjeto.trim(),
        proprietario_id: session.user.id,
        finalidade: finalidade,
        tem_filhos: temFilhos,
        tem_animais: temAnimais
      };

      if (finalidade === 'outro' && finalidadeOutro.trim()) {
        projetoData.finalidade_outro = finalidadeOutro.trim();
      }

      const { data: novoProjeto, error: projetoError } = await supabase
        .from('projetos')
        .insert(projetoData)
        .select()
        .single();

      if (projetoError) throw projetoError;

      const { error: associacaoError } = await supabase
        .from('usuarios_projetos')
        .insert({
          user_id: session.user.id,
          projeto_id: novoProjeto.id,
          role: 'admin'
        });

      if (associacaoError) {
        // Don't leave a project nobody belongs to; a retry would duplicate it.
        const { error: limpezaError } = await supabase.from('projetos').delete().eq('id', novoProjeto.id);
        if (limpezaError) console.error('Error removing orphan project:', limpezaError);
        throw associacaoError;
      }

      setStep('done');
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (error) {
      setMessage(t('createProject.errors.generic', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  if (step === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="bg-white p-6 sm:p-8 rounded-xl border border-gray-200 max-w-md w-full text-center">
          <div className="bg-verde-100 text-verde-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5">
            <Check size={28} />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-1">{t('createProject.createdTitle')}</h2>
          <p className="text-sm text-gray-500">{t('createProject.createdBody')}</p>
        </div>
      </div>
    );
  }

  if (step === 'auth') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto p-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 font-medium"
          >
            <ArrowLeft size={20} />
            {t('common.back')}
          </button>
          <Auth />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <div className="max-w-md w-full">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 font-medium"
        >
          <ArrowLeft size={20} />
          {t('common.back')}
        </button>

        <div className="bg-white p-6 sm:p-8 rounded-xl border border-gray-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-lavender-400 p-3 rounded-2xl">
              <Plus size={22} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t('createProject.title')}</h2>
              <p className="text-xs text-gray-500">{t('createProject.step2of2')}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {t('createProject.nameLabel')}
              </label>
              <input
                type="text"
                value={nomeProjeto}
                onChange={(e) => setNomeProjeto(e.target.value)}
                placeholder={t('createProject.namePlaceholder')}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-colors"
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-1.5">
                {t('createProject.nameHelp')}
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {t('createProject.purposeLabel')}
              </label>
              <select
                value={finalidade}
                onChange={(e) => setFinalidade(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-colors"
              >
                <option value="">{t('common.select')}</option>
                <option value="casal">{t('createProject.purposeCouple')}</option>
                <option value="colegas_quarto">{t('createProject.purposeRoommates')}</option>
                <option value="amigos">{t('createProject.purposeFriends')}</option>
                <option value="trabalho">{t('createProject.purposeWork')}</option>
                <option value="individual">{t('createProject.purposeIndividual')}</option>
                <option value="outro">{t('createProject.purposeOther')}</option>
              </select>
            </div>

            {finalidade === 'outro' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  {t('createProject.purposeOtherLabel')}
                </label>
                <input
                  type="text"
                  value={finalidadeOutro}
                  onChange={(e) => setFinalidadeOutro(e.target.value)}
                  placeholder={t('createProject.purposeOtherPlaceholder')}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-colors"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {t('createProject.kidsLabel')}
              </label>
              <select
                value={temFilhos}
                onChange={(e) => setTemFilhos(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-colors"
              >
                <option value="">{t('common.select')}</option>
                <option value="sim">{t('common.yes')}</option>
                <option value="nao">{t('common.no')}</option>
                <option value="prefiro_nao_responder">{t('common.preferNotToSay')}</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {t('createProject.petsLabel')}
              </label>
              <select
                value={temAnimais}
                onChange={(e) => setTemAnimais(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-colors"
              >
                <option value="">{t('common.select')}</option>
                <option value="sim">{t('common.yes')}</option>
                <option value="nao">{t('common.no')}</option>
                <option value="prefiro_nao_responder">{t('common.preferNotToSay')}</option>
              </select>
            </div>

            {message && (
              <div className="p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-700">
                {message}
              </div>
            )}

            <button
              onClick={criarProjeto}
              disabled={loading || !nomeProjeto.trim() || !finalidade || !temFilhos || !temAnimais || (finalidade === 'outro' && !finalidadeOutro.trim())}
              className="w-full bg-gray-900 text-white h-10 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm transition-all"
            >
              {loading ? t('common.creating') : (
                <>
                  <Plus size={20} />
                  {t('createProject.submit')}
                </>
              )}
            </button>
          </div>
        </div>

        <div className="mt-6 bg-gray-50 border border-gray-200 p-4 rounded-lg text-sm">
          <p className="text-sm text-gray-600">
            {t('createProject.tip')}
          </p>
        </div>
      </div>
    </div>
  );
}
